import {
    Box,
    Button,
    Typography,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import { ArrowLeft, CircleCheck, Circle, } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LoadingGif from "../components/LoadingGif";
import { useState, useEffect } from "react";
import { useHotel } from "../context/HotelContext";
import { ANIMAL_TYPES } from "./HotelAdd_Information_1";
import { SERVICES } from "./HotelAdd_Information_2";
import { SERVICE_OPTIONS, PICKUP } from "./HotelAdd_RoomInfoAdd_1";
import { AUTOMATIC_OPTIONS } from "./HotelAdd_YSubmit";

const HotelInformation = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery("(max-width:1150px)");
    const { hotelId } = useParams();
    const { fetchDraft, hotelData, saveStep, loading } = useHotel();
    const [selectedAnimals, setSelectedAnimals] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [showRepublishModal, setShowRepublishModal] = useState(false);
    const [showUnpublishModal, setShowUnpublishModal] = useState(false);
    const [hotelInfoSaved, setHotelInfoSaved] = useState(false);
    const [basicInfo, setBasicInfo] = useState({
        hotelName: "",
        address: "",
        intro: "",
        district: "",
    });
    const [contactInfo, setContactInfo] = useState({
        phoneNumber: "",
        secondPhoneNumber: "",
        email: "",
        whatsapp: "",
    });
    const [hours, setHours] = useState({
        operationStart: "",
        operationEnd: "",
        checkinTime: "",
        checkoutTime: "",
        openOnHoliday: "",
        holidayCheckinTime: "",
        holidayCheckoutTime: "",
    });
    const [additionalSelectedServices, setAdditionalSelectedServices] = useState({});
    const [servicePrices, setServicePrices] = useState({});
    const [playTime, setPlayTime] = useState(0);
    const [customServices, setCustomServices] = useState([]);
    const [confirmationMethod, setConfirmationMethod] = useState("");
    const [selectedAutoOptions, setSelectedAutoOptions] = useState([]);
    const [isPublished, setIsPublished] = useState(false);

    const pageStyle = {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#F5F5F5",
    };

    const contentStyle = {
        flex: "1 0 auto",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
        maxWidth: "1600px",
        width: "100%",
    };

    const boxStyle = {
        display: "flex",
        alignItems: "center",
        gap: 2,
        backgroundColor: "#FFFFFF",
        border: "2px solid #99A1AF",
        borderRadius: "10px",
        padding: "20px",
        transition: "border-color 0.2s ease",
    };

    const rowLabelSx = { fontSize: isMobile ? "20px" : "16px", color: "#999", mb: 0.5 };
    const rowValueSx = { fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" };

    useEffect(() => {
        if (hotelId) {
            fetchDraft(hotelId);
        }
    }, [hotelId, fetchDraft]);

    useEffect(() => {
        if (hotelData.animalTypes) {
            // Handle both nested and flat structures
            const animals = Array.isArray(hotelData.animalTypes)
                ? hotelData.animalTypes
                : hotelData.animalTypes.animalTypes || [];
            setSelectedAnimals(animals);
        }
        if (hotelData.contactInfo) {
            setContactInfo(hotelData.contactInfo);
        }
        if (hotelData.hours) {
            setHours(hotelData.hours);
        }
        if (hotelData.services) {
            // Handle both nested and flat structures
            const servicess = Array.isArray(hotelData.services)
                ? hotelData.services
                : hotelData.services.services || [];
            setSelectedServices(servicess);
        }
        if (hotelData.basicInfo) {
            setBasicInfo(hotelData.basicInfo);
        }
        if (hotelData.additionalServices) {
            const { selectedServices, servicePrices, customServices } = hotelData.additionalServices;
            setAdditionalSelectedServices(selectedServices || {});
            setPlayTime(servicePrices?.playtime || 0);
            setServicePrices(servicePrices || {});
            if (customServices && customServices.length > 0) {
                setCustomServices(customServices);
            }
        }
        if (hotelData.bookingConfirmationMethod) {
            setConfirmationMethod(hotelData.bookingConfirmationMethod.automatic ? "automatic" : "manual");
            const requireManualConfirmation = hotelData.bookingConfirmationMethod.requireManualConfirmation;
            if (Array.isArray(requireManualConfirmation) && requireManualConfirmation.length > 0) {
                const selectedKeys = [];
                const options = requireManualConfirmation[0];
                if (options.addOnServicesSelected) selectedKeys.push("addOnServicesSelected");
                if (options.specialCareRequirements) selectedKeys.push("specialCareRequirements");
                if (options.petStayingTogether) selectedKeys.push("petStayingTogether");
                if (options.LateCheckInCheckOut) selectedKeys.push("LateCheckInCheckOut");
                if (options.petDetailsNotMeetRoomRequirements) selectedKeys.push("petDetailsNotMeetRoomRequirements");
                setSelectedAutoOptions(selectedKeys);
            }
        }
        if (hotelData.isPublished === true) {
            setIsPublished(true);
        }
    }, [hotelData]);

    const EditLink = (navigationLink) => (
        <Button
            onClick={() => navigate(navigationLink)}
            sx={{
                alignSelf: "flex-start",
                color: "#31BACD",
                textTransform: "none",
                fontSize: isMobile ? "13px" : "14px",
                textDecoration: "underline",
                minWidth: "auto",
                padding: 0,
                mt: 0.5,
            }}
        >
            {t('Edit')}
        </Button>
    );

    const handleUnpublishHotel = async () => {
        try {
            const isPublishedBody = {
                "isPublished": false
            };
            await saveStep("isPublished", isPublishedBody);
            fetchDraft(hotelId);
            setIsPublished(false);
            setShowUnpublishModal(false);
        } catch (err) {
            console.error("Failed to save step:", err);
        }
    };

    const handleRepublishHotel = async () => {
        try {
            const isPublishedBody = {
                "isPublished": true
            };
            await saveStep("isPublished", isPublishedBody);
            fetchDraft(hotelId);
            setIsPublished(true);
            setShowRepublishModal(false)
            handleShowModal("saved");
        } catch (err) {
            console.error("Failed to save step:", err);
        }
    };

    const handleShowModal = (showModal) => {
        if (showModal === "unpublish") {
            setShowUnpublishModal(true);
        } else if (showModal === "saved") {
            setHotelInfoSaved(true);
        }
        else {
            setShowRepublishModal(true);
        }
    };

    return (
        <div style={pageStyle}>
            <Navbar />
            <div style={contentStyle}>
                <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
                    {/* Heading */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                            <Button
                                onClick={() => navigate("/hotel/onboarding")}
                                sx={{
                                    minWidth: "auto",
                                    width: isMobile ? "36px" : "44px",
                                    height: isMobile ? "36px" : "44px",
                                    borderRadius: "50%",
                                    backgroundColor: "#31BACD",
                                    color: "#fff",
                                    padding: 0,
                                    alignSelf: isMobile ? "flex-start" : "center",
                                    "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                                }}
                            >
                                <ArrowLeft size={isMobile ? 18 : 22} />
                            </Button>
                            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1, alignItems: isMobile ? "flex-start" : "center",}}>
                                <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                                    {t('HotelInformation')}
                                </Typography>
                                {!isPublished &&
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", backgroundColor: "#E5194A", padding: "4px 12px", borderRadius: "10px",}}>
                                        <img src="/icons/lockHotel.svg" alt="lock" style={{ width: "20px", height: "auto" }} />
                                        <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#fff", }}>
                                            {t('PausedListing')}
                                        </Typography>
                                    </Box>
                                }
                            </Box>
                        </Box>
                        <Button
                            sx={{
                                backgroundColor: "#FFB60D",
                                color: "#fff",
                                padding: "10px 12px",
                                borderRadius: "6px",
                                textTransform: "none",
                                fontSize: isMobile ? "12px" : "14px",
                                lineHeight: 1,
                                "&:hover": { backgroundColor: "#f5cc6d" },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                alignSelf: "flex-start",
                                gap: 1,
                                fontWeight: "700",
                            }}
                            onClick={() => { navigate(`/hotel/${hotelId}/documents`); }}>
                            <img src="/icons/visitDocs.svg" alt="lock" style={{ width: "20px", height: "auto" }} />
                            <span>{t('Documents')}</span>
                        </Button>
                    </Box>


                    {loading ? (
                        <LoadingGif />
                    ) : (
                        <Box sx={{ width: "100%" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('AcceptedPetTypeLabel')}</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                                            gap: 2,
                                            width: "100%",
                                        }}
                                    >
                                        {ANIMAL_TYPES.map((animal) => {
                                            const selected = selectedAnimals.includes(animal.key);
                                            return (
                                                <Box key={animal.key} sx={{ ...boxStyle, display: selected ? "flex" : "none", }}>
                                                    <img src={animal.icon} alt={t(animal.labelKey)} style={{ width: "26px", height: "auto" }} />
                                                    <Typography sx={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "500", color: "#000" }}>
                                                        {t(animal.labelKey)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                                {!isPublished && EditLink(`/hotel/add/info1/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('HotelAmenities')}</Typography>
                                <Box sx={{ flex: 1 }}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
                                            gap: 2,
                                            width: "100%",
                                        }}
                                    >
                                        {SERVICES.map((services) => {
                                            const selected = selectedServices.includes(services.key);
                                            return (
                                                <Box key={services.key} sx={{ ...boxStyle, display: selected ? "flex" : "none", }}>
                                                    <img src={services.icon} alt={t(services.labelKey)} style={{ width: "26px", height: "auto" }} />
                                                    <Typography sx={{ fontSize: isMobile ? "14px" : "16px", fontWeight: "500", color: "#000" }}>
                                                        {t(services.labelKey)}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                                {!isPublished && EditLink(`/hotel/add/info2/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: 5, }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                    <Typography sx={rowLabelSx}>{t('HotelNameLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{basicInfo.hotelName}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                    <Typography sx={rowLabelSx}>{t('HotelDistrictLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{t(basicInfo.district)}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                    <Typography sx={rowLabelSx}>{t('HotelAddressLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{basicInfo.address}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('HotelIntroLabel')}</Typography>
                                <Typography sx={rowValueSx}>{basicInfo.intro}</Typography>
                                {!isPublished && EditLink(`/hotel/add/info3/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: 3, }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                        <Typography sx={rowLabelSx}>{t('PhoneNumberLabel')}</Typography>
                                        <Typography sx={rowValueSx}>{contactInfo.phoneNumber}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                        <Typography sx={rowLabelSx}>{t('SecondPhoneNumberLabel')}</Typography>
                                        <Typography sx={rowValueSx}>{t(contactInfo.secondPhoneNumber || t('NotFilledIn'))}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                        <Typography sx={rowLabelSx}>{t('HotelEmailLabel')}</Typography>
                                        <Typography sx={rowValueSx}>{contactInfo.email}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                        <Typography sx={rowLabelSx}>{t('WhatsappLabel')}</Typography>
                                        <Typography sx={rowValueSx}>{contactInfo.whatsapp}</Typography>
                                    </Box>
                                </Box>
                                {!isPublished && EditLink(`/hotel/add/info4/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 5, }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                    <Typography sx={rowLabelSx}>{t('OperationHoursLabel')}</Typography>
                                    <Typography sx={rowValueSx}>{hours.operationStart} - {hours.operationEnd}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", }}>
                                    <Typography sx={rowLabelSx}>{t('CheckinCheckoutLabel')}</Typography>
                                    <Typography sx={rowValueSx}>
                                        {t("CheckinLabel")}: {i18n.language === 'zh' ?
                                            `${hours.checkinTime} ${t('After')}` : `${t('After')} ${hours.checkinTime}`} | {t("CheckoutLabel")}
                                        : {i18n.language === 'zh' ? `${hours.checkoutTime} ${t('After')}`
                                            : `${t('After')} ${hours.checkoutTime}`}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('OpenOnHolidayLabel')}</Typography>
                                {hours.openOnHoliday ? (
                                    <Typography sx={rowValueSx}>
                                        {t("CheckinLabel")}: {i18n.language === 'zh' ?
                                            `${hours.holidayCheckinTime} ${t('After')}` : `${t('After')} ${hours.holidayCheckinTime}`} | {t("CheckoutLabel")}
                                        : {i18n.language === 'zh' ? `${hours.holidayCheckoutTime} ${t('After')}`
                                            : `${t('After')} ${hours.holidayCheckoutTime}`}
                                    </Typography>
                                ) : (
                                    <Typography sx={rowValueSx}>
                                        {t('No')}
                                    </Typography>
                                )}
                                {!isPublished && EditLink(`/hotel/add/info5/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('AdditionalServices')}</Typography>
                                {SERVICE_OPTIONS.map((service) => {
                                    const isSelected = additionalSelectedServices[service.key];
                                    return isSelected ? (
                                        <Box key={service.key} sx={{ display: "flex", alignItems: "center", gap: 3, }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, width: "200px", }}>
                                                <Box
                                                    component="img"
                                                    src={service.icon}
                                                    alt=""
                                                    sx={{ width: 30, height: 30, flexShrink: 0 }}
                                                />
                                                <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", }}>
                                                    {t(service.labelKey)}
                                                </Typography>
                                            </Box>

                                            {/* Service price - appears when selected */}
                                            {additionalSelectedServices[service.key] && (
                                                <Typography sx={rowValueSx}>
                                                    {`(HK$${servicePrices[service.key]} ${service.unitKey === "UnitPerMinute" ? `/ ${servicePrices.playtime} ` : ''}${t(service.unitKey)})`}
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : null;
                                })}

                                {PICKUP.map((service) => (
                                    <Box key={service.key} sx={{ display: "flex", alignItems: "center", gap: 3, }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, width: "200px", }}>
                                            <Box
                                                component="img"
                                                src={service.icon}
                                                alt=""
                                                sx={{ width: 30, height: 30, flexShrink: 0 }}
                                            />
                                            <Typography sx={{ fontSize: isMobile ? "14px" : "16px", color: "#000", }}>
                                                {t(service.labelKey)}
                                            </Typography>
                                        </Box>

                                        {/* Pickup price input - appears when pickup is selected */}
                                        {additionalSelectedServices[service.key] && (
                                            <Typography sx={rowValueSx}>
                                                {`(${servicePrices[service.key]})`}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}


                                {/* Custom Services */}
                                {customServices.map((row, index) => (
                                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5, }}>
                                        <Box
                                            component="img"
                                            src={"/icons/additional-services.svg"}
                                            alt=""
                                            sx={{ width: 30, height: 30, flexShrink: 0, }}
                                        />
                                        <Typography sx={rowValueSx}>
                                            {`(${row.label})`}
                                        </Typography>
                                    </Box>
                                ))}
                                {!isPublished && EditLink(`/hotel/add/room-info-add-1/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={rowLabelSx}>{t('ConfirmationMethod')}</Typography>
                                <Typography sx={rowValueSx}>
                                    {confirmationMethod === "automatic" ? t('AutomaticOrderConfirmation') : t('ManualOrderConfirmation')}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    {AUTOMATIC_OPTIONS.map((autoOption) => {
                                        const isAutoSelected = selectedAutoOptions.includes(autoOption.key);
                                        return (
                                            <Box
                                                key={autoOption.key}
                                                sx={{
                                                    display: isAutoSelected ? "flex" : "none",
                                                    alignItems: "center",
                                                    gap: 1.5,
                                                    cursor: "pointer",
                                                    padding: "8px",
                                                    borderRadius: "6px",
                                                    transition: "background-color 0.2s",
                                                    "&:hover": { backgroundColor: "#F5F5F5" },
                                                }}
                                            >
                                                {isAutoSelected ? (
                                                    <CircleCheck size={16} color="#31BACD" style={{ flexShrink: 0 }} />
                                                ) : (
                                                    <Circle size={16} color="#999" style={{ flexShrink: 0 }} />
                                                )}
                                                <Typography sx={{ fontSize: isMobile ? "12px" : "13px", color: "#666" }}>
                                                    {t(autoOption.titleKey)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                {!isPublished && EditLink(`/hotel/add/submit/${hotelId}?edit=true`)}
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, padding: "16px 0", borderBottom: "1px solid #E0E0E0" }}>
                                <Typography sx={{ fontSize: isMobile ? "20px" : "16px", color: "#000", fontWeight: "700" }}>{t('UnpublishHotelTitle')}</Typography>
                                <Typography sx={rowLabelSx}>{t('UnpublishHotelDesc')}</Typography>
                                <Button
                                    sx={{
                                        backgroundColor: isPublished ? "#E5194A" : "#4CAF50",
                                        color: "#fff",
                                        padding: "10px 12px",
                                        borderRadius: "6px",
                                        textTransform: "none",
                                        fontSize: isMobile ? "14px" : "16px",
                                        lineHeight: 1,
                                        width: isMobile ? "100%" : "120px",
                                        "&:hover": { backgroundColor: isPublished ? "#db6984" : "#6baa6d", },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                    }}
                                    onClick={() => { handleShowModal(isPublished ? "unpublish" : "republish"); }}>
                                    <img src={isPublished ? "/icons/lockHotel.svg" : "/icons/unlockHotel.svg"} alt="lock" style={{ width: "20px", height: "auto" }} />
                                    <span>{isPublished ? t('Unpublish') : t('Republish')}</span>
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </div>

            {(showRepublishModal || showUnpublishModal || hotelInfoSaved) && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1300,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: isMobile ? 2 : 4,
                            backgroundColor: "#fff",
                            borderRadius: "16px",
                            padding: "24px",
                            width: isMobile ? "80%" : "auto",
                            maxWidth: "700px",
                            flexDirection: isMobile ? "column" : "row",
                        }}
                    >
                        <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0, }}>
                            <Box
                                component="img"
                                src="/icons/ppclogo.png"
                                alt="Pet Pet Club"
                                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
                            />
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                                <img src={showUnpublishModal ? "/icons/redLockHotel.svg" : hotelInfoSaved ? "/icons/hotelUpdated.svg" : "/icons/greenUnlockHotel.svg"} alt="lock" style={{ width: "56px", height: "auto" }} />
                                <Typography sx={{ fontSize: isMobile ? "16px" : "14px", color: "#000", fontWeight: "700" }}>
                                    {showUnpublishModal ? t('UnpublishHotelTitle') : hotelInfoSaved ? t('HotelInformationSaved') : t('RepublishHotelTitle')}
                                </Typography>
                                {!hotelInfoSaved ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "80%", }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => showUnpublishModal ? setShowUnpublishModal(false) : setShowRepublishModal(false)}
                                            sx={{
                                                backgroundColor: "#D9D9D9",
                                                color: "#fff",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                fontSize: isMobile ? "13px" : "14px",
                                                padding: "8px 16px",
                                                "&:hover": { backgroundColor: "#e6e6e6", },
                                            }}
                                        >
                                            {t('Back')}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => showUnpublishModal ? handleUnpublishHotel(hotelId) : handleRepublishHotel(hotelId)}
                                            sx={{
                                                backgroundColor: showUnpublishModal ? "#E5194A" : showRepublishModal ? "#00A63E" : "#31BACD",
                                                color: "#fff",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                fontSize: isMobile ? "13px" : "14px",
                                                padding: "8px 16px",
                                                "&:hover": { backgroundColor: showUnpublishModal ? "#db6984" : "#6ad391" },
                                            }}
                                        >
                                            {loading ? <CircularProgress size={20} color="inherit" /> : showRepublishModal ? t('Republish') : t('Unpublish')}
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "40%", }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => setHotelInfoSaved(false)}
                                            sx={{
                                                backgroundColor: "#31BACD",
                                                color: "#fff",
                                                borderRadius: "10px",
                                                textTransform: "none",
                                                fontSize: isMobile ? "13px" : "14px",
                                                padding: "8px 16px",
                                                "&:hover": { backgroundColor: "#60bac6", },
                                            }}
                                        >
                                            {t('Confirm')}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            <Footer /> 
        </div >
    );
};

export default HotelInformation;
